import { Button } from "antd";
import { CalendarOutlined } from "@ant-design/icons";
import { ExternalLink } from "lucide-react";
import { useEffect } from "react";
import AOS from "aos";
import { Link } from "react-router-dom";
import HubDiagram from "../components/sterling/HubDiagram";
import {
  ArchitectureIcon,
  CalendarCheckIcon,
  deliverableIconMap,
  faqIconMap,
  ICON_SIZE,
  OperationalGearIcon,
  painIconMap,
  processIconMap,
  trustIconMap,
} from "../components/sterling/sterlingIconMap";
import {
  kiranProfile,
  prernaProfile,
  rajivProfile,
  sterlingReviewArchitectureBg,
  sterlingReviewOperationalBg,
} from "../assets";
import {
  advisors,
  architectureItems,
  bookSessionUrl,
  deliverables,
  faqs,
  operationalItems,
  painPoints,
  processSteps,
  trustIndicators,
} from "../data/sterlingPeakReadinessContent";

const iconProps = { size: ICON_SIZE.md, strokeWidth: 1.8 };

const advisorPhotos = {
  kiran: kiranProfile,
  prerna: prernaProfile,
  rajiv: rajivProfile,
};

const sterlingAos = (animation, delay) => ({
  "data-aos": animation,
  "data-aos-duration": "500",
  "data-aos-once": "true",
  "data-aos-easing": "ease-out-cubic",
  ...(delay !== undefined ? { "data-aos-delay": String(delay) } : {}),
});

const SterlingPeakReadiness = () => {
  useEffect(() => {
    AOS.init({
      duration: 500,
      offset: 80,
      once: true,
      mirror: false,
      easing: "ease-out-cubic",
    });
    AOS.refresh();

    return () => {
      AOS.init({
        duration: 1000,
        offset: 200,
        once: false,
        mirror: true,
      });
    };
  }, []);

  return (
    <div className="sterling-page">
      {/* Hero */}
      <section className="sterling-hero">
        <div className="sterling-hero-inner">
          <div className="sterling-hero-grid">
            <div className="sterling-hero-copy" {...sterlingAos("fade-right")}>
              {/* <div className="about-section-heading sterling-hero-eyebrow">
                Sterling Peak Readiness
              </div> */}
              <h1 className="sterling-hero-title">
                Is Your IBM Sterling OMS Ready for{" "}
                <span className="accent">Holiday Peak?</span>
              </h1>
              <p className="sterling-hero-subtitle">
                Black Friday and the Holiday season expose bottlenecks that remain hidden throughout the year.
              </p>
              <p className="sterling-hero-subtitle">
              Our IBM Sterling OMS architects - each with over 20 years of Implementation and optimization experience - will review your challenges and provide practical recommendations to improve stability, scalability, and performance before peak trading begins.
              </p>
              <div className="sterling-trust-row">
                {trustIndicators.map(({ label, icon }) => {
                  const Icon = trustIconMap[icon];
                  return (
                    <div key={label} className="sterling-trust-item">
                      <span className="sterling-trust-icon">
                        <Icon {...iconProps} size={ICON_SIZE.sm} />
                      </span>
                      {label}
                    </div>
                  );
                })}
              </div>
              <div className="sterling-cta-row">
                <a
                  href={bookSessionUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    className="explore-btn"
                    shape="round"
                    icon={<CalendarOutlined />}
                    size="large"
                  >
                    Book Your Free Peak Readiness Session
                  </Button>
                </a>
              </div>
            </div>
            <div className="sterling-hero-visual" {...sterlingAos("fade-left", 100)}>
              <HubDiagram />
            </div>
          </div>
        </div>
      </section>

      {/* Why Retailers Reach Out */}
      <section className="sterling-section">
        <div className="sterling-section-inner">
          {/* <div className="about-section-heading sterling-section-eyebrow">
            Common Pain Points
          </div> */}
          <h2
            className="sterling-section-heading"
            style={{ marginBottom: "32px" }}
            {...sterlingAos("fade-up")}
          >
            Why Retailers Reach Out Before Peak
          </h2>
          <p className="sterling-section-desc" {...sterlingAos("fade-up", 100)}>
            Large retailers spend months preparing for Black Friday and Holiday trading because even minor OMS bottlenecks can lead to significant business impact. IBM Sterling OMS is designed to support complex omnichannel fulfillment, but production environments often require careful tuning, architecture validation, and operational readiness before peak events.
          </p>
          <div className="sterling-pain-grid" {...sterlingAos("fade-up", 150)}>
            {painPoints.map(({ label, icon }) => {
              const Icon = painIconMap[icon];
              return (
                <div key={label} className="sterling-pain-item">
                  <div className="sterling-pain-icon">
                    <Icon {...iconProps} size={ICON_SIZE.lg} />
                  </div>
                  <span className="sterling-pain-label">{label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What You'll Receive */}
      <section className="sterling-section sterling-section-gray">
        <div className="sterling-section-inner">
          {/* <div className="about-section-heading sterling-section-eyebrow">
            Complimentary Offer
          </div> */}
          <h2 className="sterling-section-heading" {...sterlingAos("fade-up")}>
            What You'll Receive
          </h2>
          <div className="sterling-cards-row" {...sterlingAos("fade-up", 100)}>
            {deliverables.map((item, i) => {
              const Icon = deliverableIconMap[i];
              return (
                <div key={item.number} className="sterling-card">
                  <div
                    className="sterling-card-number"
                    style={{ background: item.color }}
                  >
                    {item.number}
                  </div>
                  <div
                    className="sterling-card-icon"
                    style={{ background: item.color }}
                  >
                    <Icon {...iconProps} size={ICON_SIZE.xl} color="#ffffff" />
                  </div>
                  <h3 className="sterling-card-title">{item.title}</h3>
                  <p className="sterling-card-desc">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Review Covers */}
      <section className="sterling-section">
        <div className="sterling-section-inner">
          <h2 className="sterling-section-heading" {...sterlingAos("fade-up")}>
            Our Review Covers
          </h2>
          <div className="sterling-review-row">
            <div className="sterling-review-box" {...sterlingAos("fade-right")}>
              <div className="sterling-review-header architecture">
                <span className="sterling-review-header-icon">
                  <ArchitectureIcon {...iconProps} size={ICON_SIZE.lg} />
                </span>
                <h3>Architecture</h3>
              </div>
              <div className="sterling-review-body architecture">
                <img
                  src={sterlingReviewArchitectureBg}
                  alt=""
                  aria-hidden="true"
                  className="sterling-review-watermark"
                />
                <div className="sterling-checklist">
                  {architectureItems.map((item) => (
                    <div
                      key={item}
                      className="sterling-check-item architecture"
                    >
                      <span className="sterling-check-mark">✓</span>
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="sterling-review-box" {...sterlingAos("fade-left", 100)}>
              <div className="sterling-review-header operational">
                <span className="sterling-review-header-icon">
                  <OperationalGearIcon {...iconProps} size={ICON_SIZE.lg} />
                </span>
                <h3>Operational Readiness</h3>
              </div>
              <div className="sterling-review-body operational">
                <img
                  src={sterlingReviewOperationalBg}
                  alt=""
                  aria-hidden="true"
                  className="sterling-review-watermark"
                />
                <div className="sterling-checklist">
                  {operationalItems.map((item) => (
                    <div
                      key={item}
                      className="sterling-check-item operational"
                    >
                      <span className="sterling-check-mark">✓</span>
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Meet Your Advisors */}
      <section className="sterling-section sterling-section-purple">
        <div className="sterling-section-inner">
          <h2 className="sterling-section-heading" {...sterlingAos("fade-up")}>
            Meet Your Advisors
          </h2>
          <div className="sterling-advisors-row" {...sterlingAos("fade-up", 100)}>
            {advisors.map((advisor) => (
              <div key={advisor.title} className="sterling-advisor-card">
                <div className="sterling-advisor-avatar">
                  <img
                    src={advisorPhotos[advisor.image]}
                    alt={advisor.title}
                    className="sterling-advisor-photo"
                  />
                </div>
                <div>
                  <h3 className="sterling-advisor-title">
                    {advisor.href ? (
                      advisor.href.startsWith("http") ? (
                        <a
                          href={advisor.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="sterling-advisor-title-link"
                        >
                          {advisor.title}
                          <ExternalLink size={15} strokeWidth={2.2} aria-hidden="true" />
                        </a>
                      ) : (
                        <Link to={advisor.href} className="sterling-advisor-title-link">
                          {advisor.title}
                          <ExternalLink size={15} strokeWidth={2.2} aria-hidden="true" />
                        </Link>
                      )
                    ) : (
                      advisor.title
                    )}
                  </h3>
                  <ul className="sterling-advisor-list">
                    {advisor.qualifications.map((q) => (
                      <li key={q}>
                        <span className="sterling-advisor-check">✓</span>
                        {q}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
          <div className="sterling-advisors-banner" {...sterlingAos("fade-up", 150)}>
            Our team has led large-scale Sterling OMS implementations for
            leading retailers across North America.
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="sterling-section">
        <div className="sterling-section-inner">
          <h2 className="sterling-section-heading" {...sterlingAos("fade-up")}>
            How It Works
          </h2>
          <div className="sterling-process" {...sterlingAos("fade-up", 100)}>
            {processSteps.map((step, i) => {
              const Icon = processIconMap[step.icon];
              return (
                <div
                  key={step.number}
                  className={`sterling-process-step ${i % 2 === 0 ? "tone-blue" : "tone-purple"}`}
                >
                  <div className="sterling-process-marker">
                    <span className="sterling-process-number">{step.number}</span>
                    <div className="sterling-process-icon">
                      <Icon {...iconProps} size={ICON_SIZE.lg} />
                    </div>
                  </div>
                  <h4 className="sterling-process-title">{step.title}</h4>
                  <p className="sterling-process-desc">{step.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ + Closing CTA */}
      <section className="sterling-section sterling-section-gray sterling-closing-section">
        <div className="sterling-section-inner">
          <h2 className="sterling-section-heading" {...sterlingAos("fade-up")}>
            Frequently Asked Questions
          </h2>
          <div className="sterling-faq-grid" {...sterlingAos("fade-up", 100)}>
            {faqs.map((faq) => {
              const Icon = faqIconMap[faq.icon];
              return (
                <div key={faq.question} className="sterling-faq-item">
                  <div className="sterling-faq-icon">
                    <Icon {...iconProps} />
                  </div>
                  <div>
                    <h4 className="sterling-faq-question">{faq.question}</h4>
                    <p className="sterling-faq-answer">{faq.answer}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="sterling-final-banner" {...sterlingAos("fade-up", 100)}>
            <div className="sterling-final-inner">
              <div className="sterling-final-icon">
                <CalendarCheckIcon {...iconProps} size={ICON_SIZE.xl} />
              </div>
              <div className="sterling-final-content">
                <h2 className="sterling-final-title">
                  Let's Make Sure Peak Season Doesn't Become Firefighting Season
                </h2>
                <p className="sterling-final-desc">
                Book a confidential advisory session with our senior IBM Sterling OMS architects and receive practical recommendations tailored to your environment.
                </p>
              </div>
              <div className="sterling-final-actions">
                <a
                  href={bookSessionUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    className="process-banner-btn"
                    shape="round"
                    icon={<CalendarOutlined />}
                    size="large"
                  >
                    Book My Free Peak Readiness Session
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SterlingPeakReadiness;
